import { create } from 'zustand'

/**
 * TOUR GUIADO — progresso por USUÁRIO, nunca por empresa.
 *
 * A norma do convidado (CLAUDE.md, 02/09/2026): trilho, passo e perfil são do
 * USUÁRIO. Quem é convidado para a base de outra pessoa nunca viu o tour, mesmo
 * que a empresa já tenha visto — por isso a chave de gravação leva o `userId` e
 * NÃO o `tenantId`. Trocar um pelo outro é exatamente o defeito que a norma nomeia.
 *
 * Gravação local (localStorage). O tour é ajuda de tela: perder o registro só
 * faz o tour abrir de novo, nunca bloqueia trabalho. Sem armazenamento (aba
 * anônima, cota cheia), tudo segue em memória.
 */
export const VERSAO_DO_TOUR = 1

export type DesfechoDoTour = 'concluido' | 'pulado'

/**
 * Funções do produto que o tour apresenta, uma a uma. Cada uma tem uma dica curta
 * (ponto pulsando na tela), uma explicação própria e um lugar no checklist da Ajuda.
 */
export const ITENS_DO_TOUR = ['equipe', 'voz', 'comandos', 'indicadores'] as const
export type ItemDoTour = (typeof ITENS_DO_TOUR)[number]
export type ItensFeitos = Partial<Record<ItemDoTour, true>>

/** Pedido de mostrar UMA função (clique no ponto, `/tour voz`, botão da Ajuda). */
export interface PedidoDeItem {
  item: ItemDoTour
  /** Sobe a cada pedido; pedir o mesmo item duas vezes abre duas vezes. */
  n: number
}

interface RegistroDoTour {
  versao: number
  desfecho: DesfechoDoTour
  em: string
}

interface GuidedTourState {
  userId: string | null
  registro: RegistroDoTour | null
  /** Sobe de 1 em 1 a cada pedido de abrir o tour (`/tour`, Ajuda). O host reage à mudança. */
  pedidos: number
  ativo: boolean
  /** Funções que ESTE usuário já viu (checklist da Ajuda; apaga o ponto pulsando). */
  feitos: ItensFeitos
  /** Avisos de mudança que ESTE usuário já fechou. */
  avisosVistos: string[]
  /** Último pedido de mostrar uma função; o host reage à mudança de `n`. */
  pedidoDeItem: PedidoDeItem | null
  ajudaAberta: boolean
  /** Como fechar o tour que estiver aberto agora (completo, mini tour ou aviso). */
  fecharAtual: (() => void) | null
  definirFechamento: (fechar: (() => void) | null) => void
  carregar: (userId: string) => void
  marcarFeito: (item: ItemDoTour) => void
  marcarAvisoVisto: (id: string) => void
  pedirItem: (item: ItemDoTour) => void
  abrirAjuda: () => void
  fecharAjuda: () => void
  registrar: (desfecho: DesfechoDoTour) => void
  pedirInicio: () => void
  marcarAtivo: (ativo: boolean) => void
  /** Abre sozinho só quando ESTE usuário nunca fechou esta versão do tour. */
  deveAbrirSozinho: () => boolean
}

export function chaveDoTour(userId: string): string {
  return `axg-tour-guiado:v${VERSAO_DO_TOUR}:${userId}`
}

/** Itens vistos e avisos fechados: também por USUÁRIO (mesma norma do convidado). */
export const chaveDosItens = (userId: string) => `axg-tour-itens:v${VERSAO_DO_TOUR}:${userId}`
export const chaveDosAvisos = (userId: string) => `axg-tour-avisos:v${VERSAO_DO_TOUR}:${userId}`

function lerJson<T>(chave: string): T | null {
  try {
    const bruto = localStorage.getItem(chave)
    return bruto ? (JSON.parse(bruto) as T) : null
  } catch {
    return null
  }
}

function gravarJson(chave: string, valor: unknown): void {
  try {
    localStorage.setItem(chave, JSON.stringify(valor))
  } catch {
    /* sem armazenamento: fica só em memória nesta sessão */
  }
}

function lerItens(userId: string): ItensFeitos {
  const bruto = lerJson<Record<string, unknown>>(chaveDosItens(userId))
  const feitos: ItensFeitos = {}
  for (const item of ITENS_DO_TOUR) if (bruto?.[item] === true) feitos[item] = true
  return feitos
}

function lerAvisos(userId: string): string[] {
  const bruto = lerJson<unknown>(chaveDosAvisos(userId))
  return Array.isArray(bruto) ? bruto.filter((x): x is string => typeof x === 'string') : []
}

function ler(userId: string): RegistroDoTour | null {
  try {
    const bruto = localStorage.getItem(chaveDoTour(userId))
    if (!bruto) return null
    const r = JSON.parse(bruto) as Partial<RegistroDoTour>
    if (r.versao !== VERSAO_DO_TOUR) return null
    if (r.desfecho !== 'concluido' && r.desfecho !== 'pulado') return null
    return { versao: r.versao, desfecho: r.desfecho, em: String(r.em ?? '') }
  } catch {
    return null
  }
}

function gravar(userId: string, registro: RegistroDoTour): void {
  try {
    localStorage.setItem(chaveDoTour(userId), JSON.stringify(registro))
  } catch {
    /* sem armazenamento: fica só em memória nesta sessão */
  }
}

export const useGuidedTourStore = create<GuidedTourState>((set, get) => ({
  userId: null,
  registro: null,
  pedidos: 0,
  ativo: false,
  feitos: {},
  avisosVistos: [],
  pedidoDeItem: null,
  ajudaAberta: false,
  carregar: (userId) =>
    set({
      userId,
      registro: userId ? ler(userId) : null,
      feitos: userId ? lerItens(userId) : {},
      avisosVistos: userId ? lerAvisos(userId) : [],
    }),
  marcarFeito: (item) => {
    const { userId, feitos } = get()
    if (feitos[item]) return
    const novos: ItensFeitos = { ...feitos, [item]: true }
    if (userId) gravarJson(chaveDosItens(userId), novos)
    set({ feitos: novos })
  },
  marcarAvisoVisto: (id) => {
    const { userId, avisosVistos } = get()
    if (avisosVistos.includes(id)) return
    const novos = [...avisosVistos, id]
    if (userId) gravarJson(chaveDosAvisos(userId), novos)
    set({ avisosVistos: novos })
  },
  fecharAtual: null,
  definirFechamento: (fechar) => set({ fecharAtual: fechar }),
  // Pedido da pessoa vale mais que qualquer balão aberto: fecha o atual ANTES de pedir, para o
  // anfitrião do novo tour já encontrar a tela livre (medido no E2E real: com o aviso de mudança
  // aberto o `/tour` era ignorado).
  pedirItem: (item) => {
    get().fecharAtual?.()
    set((s) => ({ pedidoDeItem: { item, n: (s.pedidoDeItem?.n ?? 0) + 1 } }))
  },
  abrirAjuda: () => {
    get().fecharAtual?.()
    set({ ajudaAberta: true })
  },
  fecharAjuda: () => set({ ajudaAberta: false }),
  registrar: (desfecho) => {
    const { userId } = get()
    const registro: RegistroDoTour = { versao: VERSAO_DO_TOUR, desfecho, em: new Date().toISOString() }
    if (userId) gravar(userId, registro)
    set({ registro })
  },
  pedirInicio: () => {
    get().fecharAtual?.()
    set((s) => ({ pedidos: s.pedidos + 1 }))
  },
  marcarAtivo: (ativo) => set({ ativo }),
  deveAbrirSozinho: () => {
    const { userId, registro, ativo } = get()
    return Boolean(userId) && registro === null && !ativo
  },
}))
